import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import GeneralLoader from '../../loaders/GeneralLoader';
import useEnrichmentAccess from '../../../hooks/useEnrichmentAccess';
import useContactReveal from '../../../hooks/useContactReveal';

const STATUS_FOUND = 'found';
const STATUS_NOT_FOUND = 'not_found';

const DISABLED_REASON_TOOLTIPS = {
  missing_identifiers: 'Insufficient contact data for enrichment',
  insufficient_credits: 'Insufficient credits',
  not_materialised: 'Reveal not yet available for this contact'
};

/**
 * Picks the company-in-context for the Contact Detail page reveal.
 *
 * The reveal endpoint needs a single (companyId, personCrefoId) pair so the BE
 * can derive the company name (Surfe rejects a person without a company) and
 * scope the materialised contact_enrichment row. A person on the Contact Detail
 * page can have multiple company relations; we pick exactly one:
 *
 *   1. If a `backRefCompanyId` is supplied (the page was opened from a specific
 *      company's Key Contacts tab, threaded via the back-ref), prefer the
 *      relation matching it — that's the company the user was just looking at.
 *   2. Otherwise fall back to the first relation in `individual.companies` (the
 *      BE returns the primary relation first).
 *
 * Returns the raw relation object (carrying BE-2's enrichment columns) or null.
 */
const pickCompanyInContext = (companies, backRefCompanyId) => {
  if (!Array.isArray(companies) || companies.length === 0) return null;
  if (backRefCompanyId != null) {
    const matched = companies.find(
      (c) => String(c?.companyId ?? c?.id) === String(backRefCompanyId)
    );
    if (matched) return matched;
  }
  // Prefer a fully-revealable relation (carries both the company id and the
  // person crefo id) over a sparse first relation; fall back to the first.
  const viable = companies.find(
    (c) => (c?.companyId ?? c?.id) != null && c?.person_crefo_id != null
  );
  return viable || companies[0];
};

/**
 * Contact Information section on the Individual (Contact) Detail page
 * (TD-2079 AC16-18). Two reveal slots — personal email (1 credit) and mobile
 * phone (10 credits) — each with three states:
 *   - Find button   (idle, enrichment-eligible)
 *   - inline value  (previously revealed or revealed this session)
 *   - "Not found · 0 credits" (BE returned not_found)
 *
 * The company-in-context is the person's primary/first company relation (or one
 * matching the page back-ref). From that relation we read the BE-2 fields:
 * companyId, person_crefo_id, contact_enrichment_id, enrichment_status_email,
 * enrichment_status_phone, personal_email, mobile_phone.
 *
 * Reveal id = contact_enrichment_id when present, else the contact handle
 * (companyId, the relation/profile id). We ALWAYS pass companyId+personCrefoId
 * to useContactReveal so the BE can materialise the contact_enrichment row
 * lazily on first reveal.
 *
 * Wrapped in observer(): useEnrichmentAccess reads
 * profilerAccessStore.isCognitoContactEnrichment (a MobX computed).
 */
const ContactInformationSection = observer(({ individual = {}, backRefCompanyId = null }) => {
  const access = useEnrichmentAccess();

  const companies = individual?.companies || [];
  const relation = pickCompanyInContext(companies, backRefCompanyId);

  // `undefined` (not null) so useContactReveal omits them from the POST body
  // when absent — it only strips `=== undefined` keys.
  const companyId = relation?.companyId ?? relation?.id ?? undefined;
  const personCrefoId = relation?.person_crefo_id ?? individual?.crefo_id ?? undefined;
  const contactEnrichmentId = relation?.contact_enrichment_id ?? null;

  // Reveal id: the materialised contact_enrichment.id when known, otherwise the
  // contact handle (companyId/relation id). companyId+personCrefoId are always
  // passed so the BE can materialise lazily even before an id exists.
  const revealId = contactEnrichmentId ?? companyId;

  const { emailState, phoneState, revealEmail, revealPhone, insufficientCredits } = useContactReveal(
    revealId,
    { companyId, personCrefoId }
  );

  // Disabled-reason precedence (most specific first):
  //   - not_materialised:   we have no contact_enrichment_id AND can't lazily
  //     materialise (missing companyId or personCrefoId) → reveal impossible.
  //   - missing_identifiers: name/company absent → BE can't run enrichment.
  //   - insufficient_credits: company hit its credit limit (or hook saw a 402).
  const hasName = !!(
    individual?.first_name ||
    individual?.last_name ||
    individual?.fullName ||
    individual?.full_name
  );
  const canMaterialise = companyId != null && personCrefoId != null;
  const disabledReason = (() => {
    if (!contactEnrichmentId && !canMaterialise) return 'not_materialised';
    if (!hasName || companyId == null) return 'missing_identifiers';
    if (access.limitReached || insufficientCredits) return 'insufficient_credits';
    return null;
  })();
  const buttonsDisabled = !!disabledReason;

  // Inline-value precedence: cached BE value > value revealed this session.
  const cachedEmail =
    relation?.enrichment_status_email === STATUS_FOUND ? relation?.personal_email : null;
  const cachedEmailNotFound = relation?.enrichment_status_email === STATUS_NOT_FOUND;
  const revealedEmail = emailState.status === STATUS_FOUND ? emailState.value : null;
  const revealedEmailNotFound = emailState.status === STATUS_NOT_FOUND;
  const emailLoading = emailState.status === 'loading';
  const emailInline = cachedEmail || revealedEmail;
  const emailShowNotFound = !emailInline && (cachedEmailNotFound || revealedEmailNotFound);

  const cachedPhone =
    relation?.enrichment_status_phone === STATUS_FOUND ? relation?.mobile_phone : null;
  const cachedPhoneNotFound = relation?.enrichment_status_phone === STATUS_NOT_FOUND;
  const revealedPhone = phoneState.status === STATUS_FOUND ? phoneState.value : null;
  const revealedPhoneNotFound = phoneState.status === STATUS_NOT_FOUND;
  const phoneLoading = phoneState.status === 'loading';
  const phoneInline = cachedPhone || revealedPhone;
  const phoneShowNotFound = !phoneInline && (cachedPhoneNotFound || revealedPhoneNotFound);

  const renderRevealSlot = ({
    icon,
    label,
    inlineValue,
    showNotFound,
    loading,
    onClick,
    buttonLabel,
    costTooltip,
    fieldKey
  }) => {
    const tooltip = buttonsDisabled ? DISABLED_REASON_TOOLTIPS[disabledReason] : costTooltip;
    const renderSlotValue = () => {
      if (inlineValue) {
        return <span className="contact-information-value">{inlineValue}</span>;
      }
      if (showNotFound) {
        return <span className="contact-information-not-found">Not found · 0 credits</span>;
      }
      // Idle AND loading both render the button so the spinner shows INSIDE it
      // (TD-2496) instead of swapping in a detached loader. Click-guarded and
      // aria-busy while loading.
      return (
        <button
          type="button"
          className="contact-information-reveal-button"
          // aria-disabled + click guard (not native `disabled`) so the button
          // stays focusable, the sr-only disabled-reason copy stays reachable,
          // and the native title tooltip still fires. WCAG 2.1 ARIA14.
          onClick={buttonsDisabled || loading ? undefined : onClick}
          aria-disabled={buttonsDisabled || loading || undefined}
          aria-busy={loading || undefined}
          title={tooltip}
        >
          {loading ? (
            <>
              <GeneralLoader wrapClassName="contact-information-btn-loader" />
              {/* <output> carries an implicit status live-region role, so it
                  announces the in-flight reveal without an explicit role. */}
              <output className="contact-information-sr-only">Revealing {fieldKey}</output>
            </>
          ) : (
            <>
              {buttonLabel}
              {buttonsDisabled && (
                <span className="contact-information-sr-only">
                  : {DISABLED_REASON_TOOLTIPS[disabledReason]}
                </span>
              )}
            </>
          )}
        </button>
      );
    };
    return (
      <div className={`contact-information-row contact-information-row-${fieldKey}`}>
        <img className="contact-information-icon" src={icon} alt="" />
        <span className="contact-information-label">{label}</span>
        {renderSlotValue()}
      </div>
    );
  };

  // Per-company gate (after all hooks, so rules-of-hooks holds): render nothing
  // unless enrichment is enabled for BOTH the Cognito group AND this caller's
  // subscription company (access.enabled), mirroring ContactCard's showRevealSection.
  // The IndividualDetails Cognito gate is necessary but not sufficient — a company
  // with enrichment_credit_limit = NULL must not show active Find buttons that 403.
  if (!access.enabled) return null;

  return (
    <section className="contact-information-section" aria-label="Contact information">
      <h2 className="section-title">Contact information</h2>
      {renderRevealSlot({
        icon: ICON_BUTTON_ICONS.ENVELOPE,
        label: 'Work Email',
        inlineValue: emailInline,
        showNotFound: emailShowNotFound,
        loading: emailLoading,
        onClick: revealEmail,
        buttonLabel: 'Find email',
        costTooltip: '1 credit',
        fieldKey: 'email'
      })}
      {renderRevealSlot({
        icon: ICON_BUTTON_ICONS.TELEPHONE,
        label: 'Mobile phone',
        inlineValue: phoneInline,
        showNotFound: phoneShowNotFound,
        loading: phoneLoading,
        onClick: revealPhone,
        buttonLabel: 'Find mobile',
        costTooltip: '10 credits',
        fieldKey: 'phone'
      })}
    </section>
  );
});

ContactInformationSection.propTypes = {
  individual: PropTypes.shape({
    crefo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    fullName: PropTypes.string,
    full_name: PropTypes.string,
    companies: PropTypes.arrayOf(PropTypes.object)
  }),
  backRefCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ContactInformationSection;
